import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_START, FETCH_ERROR, FETCH_SUCCESS } from '@jumbo/constants/ActionTypes';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';
import authHeader from 'services/auth/jwt/auth-header';
import customAxios from 'routes/Pages/util/Api';
import { useToast } from 'context/tostifyContext';
import useUrlParam from 'services/globalHooks/useUrlParam';

const POLL_INTERVAL_MS = 60000;
const TIMBER_SALES_API = {
	get: crmId => `/Enquiry/GetEnquiryTimberSale?crmId=${crmId}`,
	add: '/Enquiry/AddEnquiryTimberSale',
	update: '/Enquiry/EditEnquiryTimberSale',
	deleteRow: '/Enquiry/DeleteEnquiryTimberSale',
};

// How many days out counts as "near" due (inclusive). Anything past today is "overdue".
const NEAR_DUE_DAYS = 3;

export default function useContactsOverviewController() {

	const { getParam, setParam, deleteParam } = useUrlParam();

	const dispatch = useDispatch();
	const continuePollingRef = useRef(true);
	const inputRef = useRef(null);
	const { showToast } = useToast();

	const [userDetails, setUserDetails] = useState();

	// Teams
	const [Teams, setTeams] = useState([]);
	const [TeamMembers, setTeamMembers] = useState([]);
	const [SelectedTeamMembers, setSelectedTeamMembers] = useState([]);
	const [selectedTeam, setSelectedTeam] = useState({});

	// Stages / tabs
	const [Stages, setStages] = useState([]);
	const [tabs, setTabs] = useState([]);
	const [teamWiseTabs, setTeamWiseTabs] = useState([]);
	const [uniqueTab, setUniqueTab] = useState([]);
	const [value, setValue] = useState('1'); // active stage-tab value

	// Contacts
	const [data, setData] = useState([]);
	const [searchData, setSearchData] = useState([]);
	const [searchKey, setSearchKey] = useState('');
	const [crmDataLoading, setCRMDataLoading] = useState(false);

	const [addMenuOpen, setAddMenuOpen] = useState(false);
	const addMenuAnchorRef = useRef(null);
	const [stageDialogOpen, setStageDialogOpen] = useState(false);

	const [notesState, setNotesState] = useState({});

	const [notesDrawer, setNotesDrawer] = useState({ open: false, row: null });

	const [openEnquiryForm, setOpenEnquiryForm] = useState(false);

	const [salesReferenceData, setSalesReferenceData] = useState(null);

	// Documents, keyed by crmId: { [crmId]: { file: [], invoice: [], loading } }
	const [docsState, setDocsState] = useState({});

	const handleOpenNotes = row => {
		setNotesDrawer({ open: true, row });
		getContactNotes(row.crmId);
	};
	const handleCloseNotes = () => setNotesDrawer({ open: false, row: null });

	// User Profile
	const getUserProfileDetails = useCallback(() => {
		dispatch({ type: FETCH_START });
		try {
			customAxios
				.get('User/GetUserProfile', { headers: authHeader() })
				.then(({ data }) => {
					setUserDetails(data);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(() => { });
		} catch (e) {
			dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
		}
	}, [dispatch]);

	// Contacts: fetch + derived fields
	const getContactExtraFields = useCallback(item => {
		const followUpDate = item['followUpDate'] ? item['followUpDate'].substring(0, 10) : '';
		const formattedFollowUpDate = followUpDate ? new Date(followUpDate).toLocaleString('en-GB').substring(0, 10) : '';

		const tempDateArr = [item['lastCallMade'], item['lastEmailSent'], item['lastNoteMade']]
			.filter(obj => (obj ? new Date(obj) : false))
			.sort((a, b) => a - b);
		const lastContactDate = tempDateArr.length > 0 ? new Date(tempDateArr[0]).toLocaleString('en-GB').substring(0, 10) : '';

		const fullName = `${item['crmContactFirst']} ${item['crmContactLast']}`;
		const addLine1 = item['crmAddressLine1'] ? `${item['crmAddressLine1']},` : '';
		const addLine2 = item['crmAddressLine2'] ? `${item['crmAddressLine2']},` : '';
		const crmAddressCity = item['crmAddressCity'] ? `${item['crmAddressCity']},` : '';
		const crmAddressTown = item['crmAddressTown'] ? `${item['crmAddressTown']},` : '';
		const zip = item['crmAddressZip'] ? `${item['crmAddressZip']},` : '';
		let fullAdd = addLine1 + addLine2 + crmAddressCity + crmAddressTown + zip;
		if (fullAdd) fullAdd = fullAdd.slice(0, -1);

		return {
			...item,
			followUpDate,
			lastContactDate,
			FullName: fullName,
			localFollowUpDate: formattedFollowUpDate,
			fullAdd,
		};
	}, []);

	const getContacts = useCallback(
		(id, isSilent = false) => {
			const currTeam = id !== undefined && typeof id !== 'object' ? id : selectedTeam?.value;
			if (currTeam === undefined) return;

			if (!isSilent) {
				setCRMDataLoading(true);
			}

			const url = 'CRM/GetContacts/?id=' + currTeam;
			try {
				customAxios
					.get(url, { headers: authHeader() })
					.then(({ data }) => {
						setCRMDataLoading(false);
						if (data && data.length > 0) {
							setData(data.map(item => getContactExtraFields(item)));
							dispatch({ type: FETCH_SUCCESS });
							continuePollingRef.current = true;
						} else {
							setData([]);
							continuePollingRef.current = false;
						}
					})
					.catch(() => {
						dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
						setCRMDataLoading(false);
					});
			} catch (e) {
				dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
				setCRMDataLoading(false);
			}
		},
		[selectedTeam?.value, getContactExtraFields, dispatch],
	);

	const getSingleContact = useCallback(
		contactID => {
			try {
				dispatch({ type: FETCH_START });
				customAxios
					.get(`CRM/GetContacts/?id=${selectedTeam.value}&crmId=${contactID}`, { headers: authHeader() })
					.then(({ data }) => {
						if (!data) return;
						const newItem = getContactExtraFields(data[0]);
						setData(prevState => updateSingleContact(newItem, prevState));
						dispatch({ type: FETCH_SUCCESS });
					})
					.catch(() => { });
			} catch (e) {
				dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
			}
		},
		[selectedTeam, getContactExtraFields, dispatch],
	);

	function updateSingleContact(newItem, prevState) {
		const newState = [...prevState];
		const recordIndex = newState.findIndex(con => con.crmId == newItem.crmId);
		if (recordIndex > -1) newState[recordIndex] = { ...newItem };
		return newState;
	}

	function getContactHandler(contactId) {
		if (contactId) getSingleContact(contactId);
		else getContacts();
	}

	const updateCrmStages = useCallback(newStages => {
		setStages(newStages);
		getUniqueTabData();
	}, []);

	const getStages = useCallback(
		id => {
			if (!id) return;
			const currTeam = id || selectedTeam.value;
			dispatch({ type: FETCH_START });
			try {
				customAxios
					.get('CRM/Get_CRM_Stages/?id=' + currTeam, { headers: authHeader() })
					.then(({ data }) => {
						if (data) {
							updateCrmStages(data);
							dispatch({ type: FETCH_SUCCESS });
						}
					})
					.catch(() => { });
			} catch (e) {
				dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
			}
		},
		[dispatch, selectedTeam, updateCrmStages],
	);

	// ─── Contact CRUD (create / edit) 
	const addNewContact = useCallback(
		async (url, payload, { coerceNullsForLegacyAdd = false } = {}) => {
			if (coerceNullsForLegacyAdd) {
				for (const key in payload) {
					if (payload[key] === undefined) payload[key] = key === 'followUpDate' ? null : '';
					else if (payload[key] === null && key !== 'followUpDate') payload[key] = 0;
				}
			}
			const isStageEmpty = payload.crmStageName === '';

			dispatch({ type: FETCH_START });
			try {
				const response = await customAxios.post(url, payload, { headers: authHeader() });
				getContacts();
				if (isStageEmpty) getStages();
				dispatch({ type: FETCH_SUCCESS });

				// NOTE: adjust this extraction if your API wraps the id
				// differently — confirmed the backend returns crmId, but the
				// exact response envelope (response.data.crmId vs
				// response.data.id vs a bare number) should be double-checked
				// against the real payload.
				const crmId =
					response?.data?.crmId ??
					response?.data?.id ??
					(typeof response?.data === 'number' ? response.data : null) ??
					payload.crmId ??
					null;

				return crmId;
			} catch (error) {
				dispatch({ type: FETCH_ERROR, payload: error.message });
				return null;
			}
		},
		[dispatch, getContacts, getStages],
	);

	function removeContactFromList(prevState, contactID) {
		return [...prevState].filter(con => con.crmId != contactID);
	}

	const updateCRMContactList = useCallback(contactID => {
		setData(prevState => removeContactFromList(prevState, contactID));
	}, []);

	const deleteContact = useCallback(
		obj => {
			dispatch({ type: FETCH_START });
			customAxios
				.post('CRM/DeleteContact', obj, { headers: authHeader() })
				.then(() => {
					updateCRMContactList(obj.ContactId);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => {
					dispatch({ type: FETCH_ERROR, payload: error.message });
				});
		},
		[dispatch, updateCRMContactList],
	);

	const deleteMultipleContact = useCallback(
		records => {
			const recordIDs = records.map(item => ({ ContactId: item.crmId, crmTeamId: selectedTeam.value }));
			dispatch({ type: FETCH_START });
			customAxios
				.post('/CRM/DeleteMultipleContacts', recordIDs, { headers: authHeader() })
				.then(() => {
					getContacts();
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => {
					dispatch({ type: FETCH_ERROR, payload: error.message });
				});
		},
		[dispatch, selectedTeam, getContacts],
	);

	// ─── Enquiry submit (Timber Enquiry form + plain contact-dialog enquiry edits) ─
	// This now does THREE things depending on what's passed in:
	//   1. Plain "just save these enquiry fields" (no contactPayload) — same
	//      behaviour as before, used by ContactFormDialog's existing
	//      edit-enquiry flow. Requires enquiryData.crmId (or options.crmId)
	//      to already be set.
	//   2. "Create a brand-new contact, then create their first enquiry"
	//      (contactPayload present, options.crmId absent/null) — used by the
	//      Timber Enquiry modal's "new contact" path.
	//   3. "Update an existing contact, then create-or-edit their enquiry"
	//      (contactPayload present, options.crmId present) — used by the
	//      Timber Enquiry modal's "existing contact" path.
	//
	// Also fixed the original bug where the crmId returned from the POST was
	// swallowed by the outer async function never returning it.
	const handleEnquiryformSubmit = useCallback(
		async (enquiryData, endpoint = '/Enquiry/AddEnquiryDetails', { crmId: existingCrmId, contactPayload, notifyEmail } = {}) => {
			try {
				let crmId = existingCrmId || enquiryData.crmId || null;

				if (contactPayload) {
					if (crmId) {
						// Existing contact — merge in whatever the user edited before
						// touching the enquiry. EditContactNew expects real nulls
						// rather than the legacy 0-coercion AddContact needs.
						await addNewContact('/CRM/EditContactNew', { ...contactPayload, crmId }, { coerceNullsForLegacyAdd: false });
					} else {
						// Brand new contact — must exist before an enquiry can
						// reference it via crmId.
						crmId = await addNewContact('/CRM/AddContact', contactPayload, { coerceNullsForLegacyAdd: true });
						if (!crmId) {
							showToast('Failed to create contact', 'error');
							return null;
						}
					}
				}

				if (!crmId) {
					showToast('A contact is required before an enquiry can be saved', 'error');
					return null;
				}

				enquiryData.crmId = crmId;

				dispatch({ type: FETCH_START });
				const response = await customAxios.post(endpoint, enquiryData, { headers: authHeader() });
				setOpenEnquiryForm(false);
				showToast('Enquiry Saved Successfully', 'success');
				dispatch({ type: FETCH_SUCCESS });
				getContacts();

				// Send email notification if notifyEmail is provided
				if (notifyEmail?.recipients?.length) {
					const uniqueRecipients = [...new Set(notifyEmail.recipients)];
					Promise.allSettled(
						uniqueRecipients.map(email =>
							customAxios.post('/GenTasks/AddNotifications', {
								sendTo: email,
								notificationSubject: notifyEmail.subject,
								notificationBody: notifyEmail.html,
								smsBody: null,
								notificationDeliveryType: 'Email',
								flag: 0,
								primaryKey: null,
								templateType: 'CRM',
								rawData: notifyEmail.html,
								notificationUniqueId: `${response?.data?.crmId || crmId}`,
								notificationSection: 'CRM',
							}, { headers: authHeader() })
						)
					); // fire-and-forget; failures shouldn't block the save flow
				}

				return response?.data?.crmId || crmId;
			} catch (Ex) {
				showToast('Failed to add enquiry', 'error');
				dispatch({ type: FETCH_ERROR, payload: Ex.message });
				console.log(Ex);
				return null;
			}
		},
		[dispatch, addNewContact, getContacts, showToast],
	);

	// ─── Documents (upload / view / delete), keyed by crmId ───────────────────
	// Moved in from EnquiryFormModal so the same logic is reusable and always
	// driven by a real, current crmId (rather than a value that might still
	// be 0 because the contact hadn't been created yet).
	const getContactDocs = useCallback(
		(crmId, isSilent = false) => {
			if (!crmId) return;
			setDocsState(prev => ({
				...prev,
				[crmId]: { file: prev[crmId]?.file || [], invoice: prev[crmId]?.invoice || [], loading: !isSilent },
			}));
			customAxios
				.get(`/CRM/GetContactDocs/${crmId}`, { headers: authHeader() })
				.then(res => {
					const grouped = (res.data || []).reduce(
						(acc, doc) => {
							const key = doc.docType === 'invoice' ? 'invoice' : 'file';
							acc[key] = [...acc[key], doc];
							return acc;
						},
						{ file: [], invoice: [] },
					);
					setDocsState(prev => ({ ...prev, [crmId]: { ...grouped, loading: false } }));
				})
				.catch(() => {
					setDocsState(prev => ({
						...prev,
						[crmId]: { file: prev[crmId]?.file || [], invoice: prev[crmId]?.invoice || [], loading: false },
					}));
					dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
				});
		},
		[dispatch],
	);

	const uploadContactDocs = useCallback(
		(crmId, files, docType = 'file') => {
			if (!crmId) {
				showToast('Save the contact before attaching files', 'error');
				return Promise.resolve(null);
			}
			if (!files || files.length === 0) return Promise.resolve(null);

			const formData = new FormData();
			formData.append('crmID', crmId);
			formData.append('docType', docType);
			files.forEach((file, i) => formData.append(`file[${i}]`, file));

			return customAxios
				.post('/CRM/AddContactDocs', formData, { headers: authHeader() })
				.then(() => getContactDocs(crmId, true))
				.catch(() => {
					showToast('Failed to upload files', 'error');
				});
		},
		[getContactDocs, showToast],
	);

	const deleteContactDoc = useCallback(
		(crmId, fileData) => {
			dispatch({ type: FETCH_START });
			return customAxios
				.post('/CRM/DeleteContactDocs', { id: fileData.id }, { headers: authHeader() })
				.then(() => {
					showToast('File deleted successfully.', 'success');
					getContactDocs(crmId, true);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(() => {
					dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
				});
		},
		[dispatch, getContactDocs, showToast],
	);

	const downloadContactDoc = useCallback(
		fileData => {
			dispatch({ type: FETCH_START });
			customAxios
				.get(`/CRM/GetContactFile?id=${fileData.id}`, { headers: authHeader(), responseType: 'blob' })
				.then(blob => {
					const url = window.URL.createObjectURL(blob.data);
					const a = document.createElement('a');
					a.href = url;
					a.download = fileData.documentName;
					document.body.appendChild(a);
					a.click();
					a.remove();
					window.URL.revokeObjectURL(url);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(() => {
					dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
				});
		},
		[dispatch],
	);

	// Global Search across all stages
	const handleSearchDataUpdate = useCallback(
		(key, customData) => {
			const toSearchData = customData || data;
			if (!key || key.trim() === '') {
				setSearchData(toSearchData);
				return;
			}
			const needle = key.toString().toLowerCase();
			const filteredRows = toSearchData.filter(
				row =>
					row.crmStageName?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmContactFirst?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmContactMiddle?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmContactLast?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmContactEmail?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmContactPhone?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmAddressCity?.toString()?.toLowerCase()?.includes(needle) ||
					row.crmCompany?.toString()?.toLowerCase()?.includes(needle),
			);
			setSearchData(filteredRows);
		},
		[data],
	);

	const searchCRMData = useCallback(
		event => {
			const key = event.target.value;
			setSearchKey(key);
			handleSearchDataUpdate(key);
		},
		[handleSearchDataUpdate],
	);

	const clearSearchData = useCallback(() => {
		if (inputRef.current) inputRef.current.focus();
		setSearchKey('');
		setSearchData(data);
	}, [data]);

	useEffect(() => {
		handleSearchDataUpdate(searchKey);
	}, [data]);

	// Teams
	const getTeamMembers = useCallback(args => {
		customAxios.get('CRMTeams/GetCRMTeamMembers/?id=' + args.value, { headers: authHeader() }).then(({ data }) => {
			if (!data) return;
			const members = data.filter(obj => obj.teamId === args.value);
			setSelectedTeamMembers(members);
			setTeamMembers(data.map(item => ({ value: item.crmUserId, label: item.email, ...item })));
		});
	}, []);

	const getTeams = useCallback(() => {
		dispatch({ type: FETCH_START });
		try {
			customAxios
				.get('CRMTeams/GetCRMTeams', { headers: authHeader() })
				.then(({ data }) => {
					if (data.length === 0) {
						dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
						return;
					}
					const team = data.map(c => ({ value: c.id, label: c.crmTeamName, IsAdmin: c.isAdmin }));
					const lastTeam = JSON.parse(localStorage.getItem('crmTeam'));
					let currTeam = team[0];
					if (lastTeam) {
						const found = team.filter(item => item.value === lastTeam.value);
						currTeam = found.length ? found[0] : team[0];
					}
					setTeams(team);
					getStages(currTeam.value);
					setSelectedTeam(currTeam);
					getTeamMembers(currTeam);
					getContacts(currTeam.value);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(() => { });
		} catch (e) {
			dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
		}
	}, [dispatch, getTeamMembers, getContacts]);

	const onTeamChangeHandler = useCallback(
		args => {
			setSelectedTeam(args);
			localStorage.setItem('crmTeam', JSON.stringify(args));
			localStorage.setItem('crmCurrStage', JSON.stringify('1'));

			const teamWise = tabs.filter(obj => obj.crmTeamId === args.value);
			const teamW = teamWise.map((obj, index) => {
				const temp = { ...obj };
				if (temp.crmTeamId === args.value) {
					temp.value = `${index + 1}`;
					return temp;
				}
				return undefined;
			});
			setTeamWiseTabs(teamW);

			if (args.value) getTeamMembers(args);
			getContacts(args?.value);
		},
		[tabs, getTeamMembers, getContacts],
	);

	const leaveTeam = useCallback(() => {
		const obj = { crmTeamId: selectedTeam.value, crmMemberUserId: userDetails?.userId };
		dispatch({ type: FETCH_START });
		try {
			customAxios
				.post('CRMTeams/DeleteTeamMember', obj, { headers: authHeader() })
				.then(() => {
					dispatch({ type: FETCH_SUCCESS });
					localStorage.removeItem('crmTeam');
					getTeams();
				})
				.catch(() => { });
		} catch (e) {
			dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
		}
	}, [dispatch, selectedTeam, userDetails, getTeams]);

	// Stages
	function getUniqueTabData() {
		const objArr = searchData.reduce((acc, cur) => [...acc.filter(obj => obj.crmStageName !== cur.crmStageName), cur], []);
		setUniqueTab(objArr);
	}

	const getFilterTabData = useCallback(
		(obj, stageName) => obj.filter(row => row.crmStageName === stageName && row.crmTeamId === selectedTeam.value),
		[selectedTeam],
	);

	const updateStagesSortOrder = useCallback(
		async orderedTabs => {
			for (let i = 0; i < orderedTabs.length; i++) {
				const editObj = [
					{
						tableName: 'CRM_Stages',
						columnName: 'SortOrder',
						newValue: `${i}`,
						primaryKeyValue: `${orderedTabs[i].StageId}`,
						crmTeamId: orderedTabs[i].crmTeamId,
					},
				];
				await customAxios.post('/CRM/Edit_CRM_Stage', editObj, { headers: authHeader() });
			}
			getStages();
		},
		[getStages],
	);

	const onDragEnd = useCallback(
		result => {
			const newTabs = Array.from(teamWiseTabs);
			const draggedTab = newTabs.splice(result.source.index, 1)[0];
			newTabs.splice(result.destination?.index, 0, draggedTab);
			updateStagesSortOrder(newTabs);
			setTeamWiseTabs(newTabs);
		},
		[teamWiseTabs, updateStagesSortOrder],
	);

	const getCurrentTabInfo = useCallback(() => {
		const tabValue = teamWiseTabs.filter(t => t.value === value);
		return tabValue.length === 0 ? {} : tabValue[0];
	}, [teamWiseTabs, value]);

	const handleTabValueChange = newValue => {
		setValue(newValue);
		localStorage.setItem('crmCurrStage', JSON.stringify(newValue));
	};

	useEffect(() => {
		if (Stages && selectedTeam) {
			const data2 = Stages.filter(stage => stage.crmTeamId == selectedTeam.value);
			const newData = data2
				.map((row, index) => ({
					id: `tab${index + 1}`,
					label: `${row.crmStageName}`,
					value: `${index + 1}`,
					content: `Content ${row.crmStageName}`,
					SortOrder: row.sortOrder,
					StageId: row.stageId,
					crmTeamId: row.crmTeamId,
				}))
				.sort((a, b) => a.SortOrder - b.SortOrder);

			setTabs(newData);
			setTeamWiseTabs(newData);

			const lastactiveTab = JSON.parse(localStorage.getItem('crmCurrStage'));
			const currDataLen = newData.length;
			if (lastactiveTab && currDataLen > 0) {
				if (currDataLen >= parseInt(lastactiveTab, 10)) {
					setValue(lastactiveTab);
				} else {
					setValue(currDataLen.toString());
					localStorage.setItem('crmCurrStage', currDataLen.toString());
				}
			}
		}
	}, [selectedTeam, Stages]);

	// Initial load
	useEffect(() => {
		getTeams();
		getUserProfileDetails();
	}, []);

	// Background Polling (Silently re-fetches without showing loader)
	useEffect(() => {
		const intervalId = setInterval(() => {
			if (continuePollingRef.current) {
				getContacts(undefined, true);
			}
		}, POLL_INTERVAL_MS);
		return () => clearInterval(intervalId);
	}, [getContacts]);

	const customizeRow = args => {
		const currDate = toLocalISO();
		const rowData = args.data;
		const followUpDate = new Date(rowData.followUpDate);
		if (args.column?.field === 'followUpDate' && currDate - followUpDate > 0) {
			args.cell.style.color = 'red';
		}
	};

	// ---- Follow-up urgency helpers ----
	// Returns 'overdue' | 'near' | 'ok' | null (null when there's no follow up date to judge)
	const getFollowUpStatus = useCallback(row => {
		if (!row?.followUpDate) return null;
		const due = new Date(row.followUpDate);
		if (isNaN(due.getTime())) return null;

		due.setHours(0, 0, 0, 0);
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
		if (diffDays < 0) return 'overdue';
		if (diffDays <= NEAR_DUE_DAYS) return 'near';
		return 'ok';
	}, []);

	// All contacts (current team, regardless of the search box) that are overdue or coming up soon,
	// soonest/most-overdue first — used to power the "Critical Contacts" menu.
	const criticalContacts = useMemo(() => {
		return data
			.filter(row => {
				const status = getFollowUpStatus(row);
				return (status === 'overdue' || status === 'near') && row?.crmStageId !== 19;
			})
			.sort((a, b) => new Date(a.followUpDate) - new Date(b.followUpDate));
	}, [data, getFollowUpStatus]);

	const handleExportToExcel = useCallback(() => {
		if (!searchData || searchData.length === 0) return;

		const hdrs = [
			'Contact Name',
			'Address',
			'County',
			'Phone',
			'Email',
			'Stage',
			'Follow Up Date',
			'Last Contact',
		];

		const esc = v => {
			if (v == null) return '';
			const s = String(v);
			return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
		};

		const csv = [
			hdrs.join(','),
			...searchData.map(r =>
				[
					r.FullName,
					r.fullAdd,
					r.crmAddressCity,
					r.crmContactPhone,
					r.crmContactEmail,
					r.crmStageName,
					r.localFollowUpDate,
					r.lastContactDate,
				]
					.map(esc)
					.join(','),
			),
		].join('\n');

		// Prepend \uFEFF BOM so Excel correctly parses special/UTF-8 characters
		const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = `Contacts_${new Date().toISOString().slice(0, 10)}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(link.href);
	}, [searchData]);


	const getContactNotes = useCallback(
		(contactId, isSilent = false) => {
			if (!contactId) return;
			setNotesState(prev => ({
				...prev,
				[contactId]: { items: prev[contactId]?.items || [], loading: !isSilent },
			}));

			const payload = {
				crmContactId: contactId,
				crmTeamId: selectedTeam.value
			}
			customAxios
				.post(`CRMNotes/GetCRMNotes`, payload, { headers: authHeader() })
				.then(({ data }) => {
					const sorted = (data || []).slice().sort((a, b) => new Date(b.noteDate) - new Date(a.noteDate));
					setNotesState(prev => ({ ...prev, [contactId]: { items: sorted, loading: false } }));
				})
				.catch(() => {
					setNotesState(prev => ({
						...prev,
						[contactId]: { items: prev[contactId]?.items || [], loading: false },
					}));
				});
		},
		[selectedTeam],
	);

	const addContactNote = useCallback(
		(row, noteText, mentionedUsers = [], actionType = 'Note', stageInfo) => {
			if (!row) return;
			if (actionType === 'Note' && !noteText) return;

			const noteField =
				actionType === 'StageChange'
					? JSON.stringify({ fromStage: stageInfo?.fromStage, toStage: stageInfo?.toStage })
					: actionType === 'StageChangeWithNote'
						? JSON.stringify({ fromStage: stageInfo?.fromStage, toStage: stageInfo?.toStage, note: noteText })
						: noteText;

			const payload = {
				contactId: row.crmId,
				accountId: userDetails?.accountId ?? 0,
				note: noteField,
				noteDate: toLocalISO(),
				crmTeamId: selectedTeam.value,
				actionType,
			};

			dispatch({ type: FETCH_START });
			customAxios
				.post('CRMNotes/AddCRMNote', payload, { headers: authHeader() })
				.then(async () => {
					if (mentionedUsers?.length) {
						try {
							await Promise.allSettled(mentionedUsers.map(async (user) => {
								const subject = `You were mentioned in a note for ${row.crmContactFirst} ${row.crmContactLast}`;
								// Use noteText (plain), never noteField (may be JSON), in the email body
								const body = `You were mentioned in a note for ${row.crmContactFirst} ${row.crmContactLast}:\n\n"${noteText}"\n\nPlease check the CRM for more details.`;
								await customAxios.post('/GenTasks/AddNotifications', {
									sendTo: user.email,
									notificationSubject: subject,
									notificationBody: body,
									smsBody: null,
									notificationDeliveryType: 'Email',
									flag: 0,
									primaryKey: null,
									templateType: 'CRM',
									rawData: body,
									notificationUniqueId: `${row.crmId}-${Date.now()}`,
									notificationSection: 'CRM',
								}, { headers: authHeader() });
							}));
						} catch (notifyError) {
							console.error('Failed to send mention notifications', notifyError);
						}
					}
					getContactNotes(row.crmId, true);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => dispatch({ type: FETCH_ERROR, payload: error.message }));
		},
		[dispatch, selectedTeam, userDetails, getContactNotes],
	);

	const updateContactStage = useCallback(
		async ({ contact, newStage, note, followUpDate }) => {
			if (!contact || !newStage) return;

			// 1. Optimistic local UI update
			setData(prevData =>
				prevData.map(c =>
					c.crmId === contact.crmId
						? {
							...c,
							crmStageName: newStage.label,
							crmStageId: newStage.StageId,
							...(followUpDate && {
								followUpDate,
								localFollowUpDate: new Date(followUpDate).toLocaleString('en-GB').substring(0, 10),
							}),
						}
						: c
				)
			);

			// 2. Build single-object payload for Stage ID
			const stagePayload = {
				tableName: 'CRM_Contacts',
				columnName: 'CrmStageId',
				newValue: String(newStage.StageId),
				primaryKeyValue: String(contact.crmId),
				crmTeamId: selectedTeam.value,
			};

			try {
				// 3. Make API call 1: Update Stage
				await customAxios.post('CRM/EditContact', stagePayload, { headers: authHeader() });

				// 4. Make API call 2: Update Follow-up Date (if provided)
				if (followUpDate) {
					const followUpPayload = {
						tableName: 'CRM_Contacts',
						columnName: 'FollowUpDate',
						newValue: followUpDate,
						primaryKeyValue: String(contact.crmId),
						crmTeamId: selectedTeam.value,
					};
					await customAxios.post('CRM/EditContact', followUpPayload, { headers: authHeader() });
				}

				const actionType = note ? 'StageChangeWithNote' : 'StageChange';
				addContactNote(
					contact,
					note || '',
					[],
					actionType,
					{ fromStage: contact.crmStageName || 'Unassigned', toStage: newStage.label }
				);

				// 6. Silent sync
				getContacts(undefined, true);
			} catch (error) {
				dispatch({ type: FETCH_ERROR, payload: 'Failed to update stage: ' + error.message });
				getContacts(undefined, true);
			}
		},
		[setData, selectedTeam, dispatch, addContactNote, getContacts]
	);

	// ---- Single-contact delete wrapper (shapes payload the way DeleteContact expects) ----
	const handleDeleteContact = useCallback(
		row => {
			deleteContact({ ContactId: row.crmId, crmTeamId: selectedTeam.value });
		},
		[deleteContact, selectedTeam],
	);

	// ---- Stage management (Add / Edit / Delete) ----
	const addStage = useCallback(
		stageName => {
			if (!stageName?.trim()) return;
			dispatch({ type: FETCH_START });
			customAxios
				.post('CRM/Add_CRM_Stage', { crmTeamId: selectedTeam.value, stageDescription: stageName.trim(), stageName: stageName.trim() }, { headers: authHeader() })
				.then(() => {
					getStages();
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => dispatch({ type: FETCH_ERROR, payload: error.message }));
		},
		[dispatch, selectedTeam, getStages],
	);

	const editStageName = useCallback(
		(stage, newName) => {
			if (!newName?.trim()) return;
			dispatch({ type: FETCH_START });
			const editObj = [
				{
					tableName: 'CRM_Stages',
					columnName: 'CrmStageName',
					newValue: newName.trim(),
					primaryKeyValue: `${stage.StageId}`,
					crmTeamId: stage.crmTeamId,
				},
			];
			customAxios
				.post('/CRM/Edit_CRM_Stage', editObj, { headers: authHeader() })
				.then(() => {
					getStages();
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => dispatch({ type: FETCH_ERROR, payload: error.message }));
		},
		[dispatch, getStages],
	);

	const deleteStage = useCallback(
		stage => {
			const contactsInStage = getFilterTabData(data, stage.label);
			if (contactsInStage.length > 0) {
				dispatch({ type: FETCH_ERROR, payload: 'Move all contacts out of this stage before deleting it.' });
				return;
			}
			dispatch({ type: FETCH_START });
			customAxios
				.post('CRM/Delete_CRM_Stage', { stageId: stage.StageId, teamId: stage.crmTeamId }, { headers: authHeader() })
				.then(() => {
					getStages();
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => dispatch({ type: FETCH_ERROR, payload: error.message }));
		},
		[dispatch, data, getFilterTabData, getStages],
	);

	const handleOpenAddEnquiry = () => {
		setOpenEnquiryForm(true)
	};

	const handleCloseAddEnquiry = () => {
		setOpenEnquiryForm(false)
	};


	const deleteContactNote = useCallback(
		(row, note) => {
			dispatch({ type: FETCH_START });
			customAxios
				.post('CRMNotes/DeleteCRMNote', { NoteId: note.noteId, crmTeamId: selectedTeam.value }, { headers: authHeader() })
				.then(() => {
					getContactNotes(row.crmId, true);
					dispatch({ type: FETCH_SUCCESS });
				})
				.catch(error => dispatch({ type: FETCH_ERROR, payload: error.message }));
		},
		[dispatch, selectedTeam, getContactNotes],
	);

	// "Latest enquiry" — always treated as the single enquiry record for a
	// contact. If the backend ever starts returning more than one row here,
	// this sorts by submissionDate desc and takes the most recent.
	const getEnquiryDetails = async (crmId) => {
		const response = await customAxios.get(`/Enquiry/GetEnquiryDetails?crmID=${crmId}`, {
			headers: authHeader()
		})

		if (response.status === 200 && response.data?.length > 0) {
			const sorted = [...response.data].sort(
				(a, b) => new Date(b.submissionDate) - new Date(a.submissionDate)
			);
			return sorted[0];
		} else {
			return null;
		}
	}

	const getTimberSales = async crmId => {
		if (!crmId) return null;
		try {
			const response = await customAxios.get(TIMBER_SALES_API.get(crmId), { headers: authHeader() });
			const data = response.data;
			const rows = Array.isArray(data) ? data : data?.rows || (data ? [data] : []);
			if (rows.length === 0) return null;
			const firstRow = rows[0];
			return {
				id: data?.id ?? data?.timberSaleId ?? firstRow.timberSaleId ?? firstRow.id ?? null,
				rows,
			};
		} catch (error) {
			dispatch({ type: FETCH_ERROR, payload: error.message });
			return null;
		}
	};

	const deleteTimberSaleRow = async id => {
		try {
			await customAxios.post(TIMBER_SALES_API.deleteRow, { timberSaleId: id }, { headers: authHeader() });
			return true;
		} catch (error) {
			dispatch({ type: FETCH_ERROR, payload: error.message });
			return false;
		}
	};

	const handleTimberSaleSubmit = async (payload, docs) => {
		try {
			const crmId = await handleEnquiryformSubmit(payload.enquiryData, payload.enquiryEndpoint, {
				crmId: payload.crmId,
				contactPayload: payload.contactPayload,
			});
			if (!crmId) return null;

			const enquiry = await getEnquiryDetails(crmId);
			const enquiryId = enquiry?.id ?? enquiry?.enquiryId;
			if (enquiryId == null) {
				showToast('Could not find the saved enquiry ID for Timber Sale rows', 'error');
				return null;
			}

			const salesPayload = {
				crmId,
				rows: payload.sales.rows,
			};
			await Promise.all(salesPayload.rows.map(row => {
				const timberSaleId = row.timberSaleId ?? row.id;
				const rowPayload = {
					...(timberSaleId != null ? { timberSaleId } : {}),
					crmId,
					enquiryId,
					sP_WHNumber: payload.enquiryData.sP_WHNumber || '',
					nameLocation: row.nameLocation || '',
					productCode: row.productCode || '',
					diameter: Number(row.diameter) || 0,
					eurPerUOM: Number(row.eurPerUOM) || 0,
					unitOfMeasure: row.unitOfMeasure || '',
					completed: row.completed === true || row.completed === 'Yes' ? 'Yes' : 'No',
					quality: row.quality || '',
					vol: Number(row.vol) || 0,
					weeklyVolume: Number(row.weeklyVolume) || 0,
				};
				const endpoint = timberSaleId != null ? TIMBER_SALES_API.update : TIMBER_SALES_API.add;
				return customAxios.post(endpoint, rowPayload, { headers: authHeader() });
			}));

			if (docs?.length) await uploadContactDocs(crmId, docs, 'file');
			showToast('Timber Sale Saved Successfully', 'success');
			return { crmId };
		} catch (error) {
			dispatch({ type: FETCH_ERROR, payload: error.message });
			showToast('Failed to save Timber Sale', 'error');
			return null;
		}
	}



	return {
		inputRef,
		continuePollingRef,
		userDetails,
		Teams,
		TeamMembers,
		SelectedTeamMembers,
		selectedTeam,
		setSelectedTeam,
		onTeamChangeHandler,
		getTeams,
		getTeamMembers,
		leaveTeam,
		Stages,
		setStages,
		tabs,
		teamWiseTabs,
		setTeamWiseTabs,
		uniqueTab,
		value,
		setValue: handleTabValueChange,
		getStages,
		updateCrmStages,
		onDragEnd,
		getFilterTabData,
		getCurrentTabInfo,
		data,
		setData,
		searchData,
		setSearchData,
		searchKey,
		crmDataLoading,
		getContacts,
		getContactHandler,
		getSingleContact,
		getContactExtraFields,
		updateCRMContactList,
		addNewContact,
		deleteContact,
		deleteMultipleContact,
		searchCRMData,
		clearSearchData,
		handleSearchDataUpdate,
		customizeRow,
		handleExportToExcel,
		// Follow-up urgency
		NEAR_DUE_DAYS,
		getFollowUpStatus,
		criticalContacts,
		// Quick stage change
		updateContactStage,
		// Single-contact delete wrapper
		handleDeleteContact,
		// Stage management
		addStage,
		editStageName,
		deleteStage,
		// Add Enquiry
		handleOpenAddEnquiry,
		openEnquiryForm,
		handleCloseAddEnquiry,
		handleEnquiryformSubmit,
		// Add menu
		addMenuOpen,
		setAddMenuOpen,
		addMenuAnchorRef,
		stageDialogOpen,
		setStageDialogOpen,

		notesState,
		getContactNotes,
		addContactNote,
		deleteContactNote,

		notesDrawer,
		handleOpenNotes,
		handleCloseNotes,

		getEnquiryDetails,

		// Documents
		docsState,
		getContactDocs,
		uploadContactDocs,
		deleteContactDoc,
		downloadContactDoc,

		getParam, setParam, deleteParam,

		handleTimberSaleSubmit,
		getTimberSales,
		deleteTimberSaleRow,
		salesReferenceData
	};
}